<template>
	<div style="display: contents;">
		<template v-if="buttonComponent == 'v-btn'">
			<v-tooltip top>
				<template v-slot:activator="{ on: tooltip }">
					<v-btn
						v-on="{ ...tooltip }"
						:icon="true"
						:disabled="disabled"
						@click="openDialog()"
					>
						<v-icon>{{ button.icon || 'mdi-warehouse' }}</v-icon>
					</v-btn>
				</template>
				<span>{{ button.tooltip || 'Rettifica Magazzino' }}</span>
			</v-tooltip>
		</template>

		<v-list-item
			v-else-if="buttonComponent == 'v-list-item'"
			dense
			:disabled="disabled"
			@click.stop="openDialog()"
			ripple
		>
			<v-list-item-icon>
				<v-icon style="font-size: 18px">{{ button.icon || 'mdi-warehouse' }}</v-icon>
			</v-list-item-icon>
			<v-list-item-content>
				<v-list-item-title>{{ button.tooltip || 'Rettifica Magazzino' }}</v-list-item-title>
			</v-list-item-content>
		</v-list-item>

		<v-dialog
			v-model="dialog"
			:fullscreen="$vuetify.breakpoint.mdAndDown"
			width="680"
			persistent
			eager
		>
			<v-card class="background-2" :class="{'round-card': $vuetify.breakpoint.lgAndUp}">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">Rettifica Magazzino</p>
					<v-spacer></v-spacer>
					<v-btn icon @click.native.stop="closeDialog()"><v-icon>close</v-icon></v-btn>
				</v-card-title>
				<v-divider></v-divider>

				<v-card-text class="pa-4">
					<v-row>
						<v-col cols="12" v-if="isVariableProduct">
							<v-autocomplete
								class="variant-autocomplete"
								v-model="form.variant_id"
								:items="variantOptions"
								item-text="text"
								item-value="value"
								:search-input.sync="variantSearch"
								:loading="loadingVariants"
								:no-data-text="variantNoDataText"
								label="Variante"
								prepend-inner-icon="mdi-magnify"
								hint="Digita almeno 2 caratteri per cercare e selezionare la variante"
								persistent-hint
								outlined
								dense
								clearable
								:return-object="false"
							>
								<template v-slot:item="{ item }">
									<div>
										<div>{{ item.text }}</div>
										<div class="caption grey--text">{{ item.subtitle }} | Giacenza: {{ formatStock(item.stock) }}</div>
									</div>
								</template>
								<template v-slot:selection="{ item }">
									<span>{{ item.text }} ({{ formatStock(item.stock) }})</span>
								</template>
							</v-autocomplete>
						</v-col>

						<v-col cols="12" md="6">
							<v-select
								v-model="form.warehouse_id"
								:items="warehouseOptions"
								item-text="text"
								item-value="value"
								label="Magazzino"
								:menu-props="{'offset-y': true}"
								outlined
								dense
							></v-select>
						</v-col>

						<v-col cols="12" md="6">
							<gmh-date-picker
								v-model="form.date"
								label="Data"
								editable
							></gmh-date-picker>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="form.quantity"
								type="text"
								inputmode="decimal"
								label="Quantità impostata"
								hint="Inserisci la quantità finale desiderata nel magazzino selezionato"
								persistent-hint
								outlined
								dense
								@keydown="preventInvalidNumberInput"
								@blur="normalizeQuantity"
							></v-text-field>
						</v-col>

						<v-col cols="12">
							<v-textarea
								v-model="form.note"
								label="Nota / Motivazione rettifica"
								hint="Campo facoltativo, salvato in modo persistente con la rettifica"
								persistent-hint
								outlined
								dense
								rows="2"
								auto-grow
							></v-textarea>
						</v-col>

						<v-col cols="12">
							<v-alert type="info" outlined dense>
								<div>Giacenza attuale: <strong>{{ formattedCurrentStock }}</strong></div>
								<div>Variazione calcolata: <strong>{{ formattedDeltaQuantity }}</strong></div>
							</v-alert>
						</v-col>
					</v-row>
				</v-card-text>

				<v-divider></v-divider>
				<v-card-actions>
					<v-btn @click="closeDialog()" color="primary" text>Annulla</v-btn>
					<v-spacer></v-spacer>
					<v-btn @click="saveAdjustment()" :loading="saving" color="info" :disabled="!canSave">Salva Rettifica</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
export default {
	name: 'product-stock-adjustment-dialog',
	props: {
		button: Object,
		buttonComponent: { type: String, default: 'v-btn' },
		model: Object,
		disabled: Boolean,
	},

	data() {
		return {
			dialog: false,
			saving: false,
			loadingVariants: false,
			loadingStock: false,
			minVariantSearchChars: 2,
			variantSearch: '',
			variantOptions: [],
			warehouseOptions: [],
			currentStock: 0,
			variantSearchTimer: null,
			form: {
				warehouse_id: null,
				quantity: '0',
				date: moment().format('YYYY-MM-DD'),
				variant_id: null,
				note: '',
			},
		}
	},

	computed: {
		isVariableProduct() {
			return this.model && this.model.product_type == 'variable'
		},

		targetProductId() {
			if (this.isVariableProduct) {
				return this.getVariantId()
			}

			return this.model ? this.model.id : null
		},

		parsedQuantity() {
			return Number((this.form.quantity || '').toString().replace(',', '.'))
		},

		deltaQuantity() {
			const target = Number.isFinite(this.parsedQuantity) ? this.parsedQuantity : 0
			return target - Number(this.currentStock || 0)
		},

		formattedCurrentStock() {
			return this.formatQty(this.currentStock)
		},

		formattedDeltaQuantity() {
			return this.formatQty(this.deltaQuantity)
		},

		variantNoDataText() {
			if (this.loadingVariants) {
				return 'Ricerca in corso...'
			}

			if (!this.variantSearch || this.variantSearch.trim().length < this.minVariantSearchChars) {
				return 'Digita almeno 2 caratteri per cercare le varianti'
			}

			return 'Nessuna variante trovata'
		},

		canSave() {
			if (!this.form.warehouse_id) {
				return false
			}

			if (this.isVariableProduct && !this.form.variant_id) {
				return false
			}

			if (this.isVariableProduct && !this.getVariantId()) {
				return false
			}

			const quantity = this.parsedQuantity
			if (!Number.isFinite(quantity)) {
				return false
			}

			return Math.abs(quantity) <= 1000000000
		},
	},

	watch: {
		variantSearch(value) {
			if (!this.isVariableProduct) {
				return
			}

			if (this.variantSearchTimer) {
				clearTimeout(this.variantSearchTimer)
			}

			this.variantSearchTimer = setTimeout(() => {
				this.searchVariants(value)
			}, 300)
		},

		targetProductId() {
			this.loadCurrentStock()
		},

		'form.warehouse_id'() {
			this.loadCurrentStock()
		},

		'form.date'() {
			this.loadCurrentStock()
		},
	},

	methods: {
		openDialog() {
			this.dialog = true
			this.resetForm()
			this.loadInfo()

			if (this.isVariableProduct) {
				this.searchVariants('')
			}
		},

		closeDialog() {
			this.dialog = false
			this.saving = false
		},

		resetForm() {
			this.form = {
				warehouse_id: null,
				quantity: '0',
				date: moment().format('YYYY-MM-DD'),
				variant_id: null,
				note: '',
			}
			this.currentStock = 0
			this.variantSearch = ''
		},

		formatQty(value) {
			return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 4 }).format(Number(value || 0))
		},

		formatStock(value) {
			return this.formatQty(value)
		},

		loadInfo() {
			this.axios.get('info-products-inventory-adjustment', {
				params: {
					product_id: this.model.id,
				},
			}).then(response => {
				const data = response.data.data || {}
				this.warehouseOptions = data.warehouses || []
				this.form.warehouse_id = data.default_warehouse_id || (this.warehouseOptions[0] ? this.warehouseOptions[0].value : null)
				this.loadCurrentStock()
			}, () => {
				this.$store.commit('setSnackbar', { timeout: 6000, text: 'Errore nel caricamento dati rettifica', value: true, color: 'error' })
			})
		},

		searchVariants(search) {
			if (!this.dialog || !this.isVariableProduct) {
				return
			}

			if (!search || search.trim().length < this.minVariantSearchChars) {
				this.variantOptions = []
				this.loadingVariants = false
				return
			}

			this.loadingVariants = true
			this.axios.get('async-products', {
				params: {
					search: search.trim(),
					type: 'variant',
					parent_id: this.model.id,
				},
			}).then(response => {
				this.variantOptions = response.data.options || []
				this.loadingVariants = false
			}, () => {
				this.loadingVariants = false
			})
		},

		loadCurrentStock() {
			if (!this.dialog || !this.form.warehouse_id || !this.targetProductId) {
				this.currentStock = 0
				return
			}

			this.loadingStock = true
			this.axios.get('products-stocks/', {
				params: {
					product_id: this.targetProductId,
				},
			}).then(response => {
				const rows = response.data.data || []
				const currentWarehouseStock = rows.find(row => row.warehouse_id == this.form.warehouse_id)
				this.currentStock = currentWarehouseStock ? Number(currentWarehouseStock.stock || 0) : 0
				this.loadingStock = false
			}, () => {
				this.loadingStock = false
				this.currentStock = 0
			})
		},

		saveAdjustment() {
			if (!this.canSave) {
				this.$store.commit('setSnackbar', { timeout: 6000, text: 'Compilare i campi obbligatori', value: true, color: 'warning' })
				return
			}

			this.saving = true
			this.axios.post('products-inventory-adjustment', {
				product_id: this.model.id,
				warehouse_id: this.form.warehouse_id,
				quantity: this.parsedQuantity,
				date: this.form.date,
				variant_id: this.getVariantId(),
				note: this.form.note,
			}).then(response => {
				const data = response.data.data || {}
				const delta = this.formatQty(data.delta_quantity)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Rettifica salvata. Variazione applicata: ' + delta,
					value: true,
					color: 'success',
				})
				this.saving = false
				this.closeDialog()
				this.$eventBusUtility.$emit('refreshShow')
			}, response => {
				const message = response && response.data && response.data.message ? response.data.message : 'Errore durante il salvataggio della rettifica'
				this.$store.commit('setSnackbar', { timeout: 7000, text: message, value: true, color: 'error' })
				this.saving = false
			})
		},

		preventInvalidNumberInput(event) {
			if (['e', 'E', '+'].includes(event.key)) {
				event.preventDefault()
			}
		},

		normalizeQuantity() {
			let value = (this.form.quantity || '').toString().replace(',', '.').trim()

			if (value === '' || value === '-' || value === '.') {
				this.form.quantity = '0'
				return
			}

			const parsed = Number(value)
			if (!Number.isFinite(parsed)) {
				this.form.quantity = '0'
				return
			}

			const maxAbs = 1000000000
			const clamped = Math.max(Math.min(parsed, maxAbs), -maxAbs)
			this.form.quantity = clamped.toString()
		},

		getVariantId() {
			const value = this.form.variant_id
			if (!value) {
				return null
			}

			if (typeof value === 'object') {
				return value.value || value.id || null
			}

			return value
		},
	},
}
</script>

<style scoped>
::v-deep .variant-autocomplete .v-input__prepend-inner {
	align-self: center;
	margin-top: 0 !important;
}

::v-deep .variant-autocomplete .v-input__prepend-inner .v-icon {
	margin-top: 0 !important;
}
</style>
